/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import CLog, { LogTypes } from './ComponentLogger';
import Decorators from './Decorators';
import {extractErrorMessage} from "./Misc";

@cc._decorator.ccclass
export default abstract class ComponentBase extends cc.Component
{
    public static readonly CustomPropertyKey: string = 'CustomPropertyInstance' + Date.now();

    private _isFirstUpdate: boolean = true;

    protected isLoaded: boolean = false;
    protected isStarted: boolean = false;

    protected isContextSet: boolean = false;

    protected setContexts(): void { this.isContextSet = true; }
    protected destructors: Function[] = [];

    /** @hidden */
    protected onLoad(): void
    {
        this.isLoaded = true;
        Decorators.PerformBinding(this);

        this.setContexts();
    }

    /** @hidden */
    protected start(): void { this.isStarted  = true; }

    /** @hidden */
    protected update(dt: number): void
    {
        if (!this._isFirstUpdate) { return; }

        if (this.firstUpdate) { this.firstUpdate(dt); }
        this._isFirstUpdate = false;
    }

    protected firstUpdate(dt: number): void { /* Placeholder */ }

    protected onDestroy(): void {
        try
        {
            this.destructors.forEach(destructor => destructor());
        }
        catch (err)
        {
            CLog.error(`Error onDestroy: [${extractErrorMessage(err)}]`);
        }
        this.destructors.length = 0;
    }

    /**
     * Helper for binding to node events and onDestroy handling.
     * @param event Event to trigger on.
     * @param fnOnEvent [[Function]] to trigger.
     */
    protected onNodeEvent(
        event: string,
        fnOnEvent: (event: cc.Event.EventCustom) => void): Function
    {
        return this.onTargetEvent(this.node, event, fnOnEvent);
    }

    /**
     * Helper for binding events to [[cc.EventTarget]] and onDestroy handling.
     * @param event Event to trigger on.
     * @param fnOnEvent [[Function]] to trigger.
     * @param target [[cc.EventTarget]] event target.
     */
    protected onTargetEvent(
        target: cc.EventTarget,
        event: string,
        fnOnEvent: (event: cc.Event.EventCustom | KeyboardEvent | cc.Event.EventTouch) => void): Function
    {
        if (target == null) { return; }
        target.on(event, fnOnEvent);

        const destructor: Function = () => target && target.off(event, fnOnEvent);
        this.destructors.push(destructor);
        return destructor;
    }

    /**
     * Helper to create [[cc.Component.EventHandler]].
     * @param componentName Name of the component class.
     * @param handlerName  Name of function handling event.
     * @param handler  Function to handle the event.
     * @param addToEventList List of [[cc.Component]] to add new handler to.
     * @param targetNode Node to target, default: this.node.
     */
    public AddComponentEventHandler(
        componentName: string,
        handlerName: string,
        handler: (args: any) => any,
        addToEventList: cc.Component.EventHandler[],
        targetNode: cc.Node = this.node
    ): cc.Component.EventHandler
    {
        // Create Slider Handler
        const componentHandler: cc.Component.EventHandler = new cc.Component.EventHandler();
        componentHandler.component = componentName;
        componentHandler.target = targetNode;
        componentHandler.handler = handlerName;

        if (this[handlerName])
        {
            CLog.warn(`Component already has property [ ${handlerName} ]`, LogTypes.Initialization);
        }
        this[handlerName] = handler;

        if (addToEventList) { addToEventList.push(componentHandler); }

        return componentHandler;
    }

    // #region Static Functions

    public static GetOrAddComponent<T extends cc.Component>(node: cc.Node | cc.Component, c: { new(): T }): T
    {
        return node.getComponent(c) || node.addComponent(c);
    }

    public static GetFirstComponent<T extends cc.Component>(
        target: cc.Node,
        ...components: ({ new(): cc.Component })[]): T
    {
        for (let component of components)
        {
            let comp: cc.Component = target.getComponent(component);
            if (comp) { return comp as T; }
        }
        return null;
    }

    public static CancelEventHandler = (event: cc.Event.EventCustom, bubbles: boolean = false) =>
    {
        if (!event) { return; }

        event.bubbles = bubbles;
        if (event.stopPropagationImmediate) { event.stopPropagationImmediate(); }
    }

    //#endregion

    // #region Instance Helpers
    protected getFirstParent<T extends cc.Component>( c: { new(): T }, node: cc.Node = this.node): T
    {
        const scene: cc.Scene = cc.director.getScene();
        let currentNode: cc.Node = node;

        while (currentNode.parent)
        {
            currentNode = currentNode.parent;
            if (currentNode === scene) { return null; }

            const component: T = currentNode.getComponent<T>(c);
            if (component) { return component; }
        }

        return null;
    }

    // #endregion

    // #region Node Helpers

    public static ReplaceChildren(target: cc.Node, source: cc.Node): void
    {
        // If not syncing children, steal them.
        const children: cc.Node[] = source.children.slice(0);
        target.removeAllChildren();

        children.forEach(child =>
        {
            child.removeFromParent(false);
            target.addChild(child);
        });
    }

    /**
     * Returns path of node.
     */
    public static GetIndexedNodePath(node: cc.Node, top: { new (): cc.Component } | cc.Node): string
    {
        let parent: cc.Node = node.parent;
        let path: string = this.GetNodeNameAndIndex(node);

        let parentIsValid: () => boolean;

        // Top is node.
        if (top instanceof cc.Node)
        {
            parentIsValid = () => parent && parent !== top;
        }
        // Top is component.
        else
        {
            parentIsValid = () => parent && !parent.getComponent(top);
        }

        while (parentIsValid())
        {
            path = `${this.GetNodeNameAndIndex(parent)}/${path}`;
            parent = parent.parent;
        }

        return path;
    }

    public static GetNodeNameAndIndex(node: cc.Node): string
    {
        return `${node.name}[${this.GetNodeIndex(node, true)}]`;
    }

    public static GetNodeIndex(target: cc.Node, sameName: boolean = false): number
    {
        const parent: cc.Node = target.parent;

        if (!parent) { return 0; }

        const siblings: cc.Node[] = sameName
            ? parent.children.filter(child => child.name === target.name)
            : parent.children;

        return siblings.indexOf(target);
    }

    /**
     * Filters out all child nodes, returning new array with siblings.
     * @param nodes [[cc.Node]] array to filter.
     * @returns [[cc.Node]] array of siblings without children.
     */
    public static FilterChildren(nodes: cc.Node[]): cc.Node[]
    {
        const siblings: cc.Node[] = [];
        for (let node of nodes)
        {
            let isChild: boolean = false;

            // Check if child of any in out list.
            for (let checkNode of nodes)
            {
                // isChildOf returns true if nodes are equivalent.
                if (checkNode === node) { continue; }
                // Check and flag child.
                if (node.isChildOf(checkNode))
                {
                    isChild = true;
                    break;
                }
            }

            if (!isChild)
            {
                siblings.push(node);
            }
        }
        return siblings;
    }

    // #region Instance Helpers
    public static GetFirstParent<T extends cc.Component>(node: cc.Node, c: { new(): T }): T
    {
        const scene: cc.Scene = cc.director.getScene();
        let component: T = null;

        while (node.parent)
        {
            node = node.parent;
            if (node === scene) { return null; }

            component = node.getComponent<T>(c);
            if (component) { break; }
        }

        return component;
    }

    // #endregion

    // #region Custom Properties

    /**
     * Property decorator for declaring a property as custom.
     */
    public static CustomProperty(): (target: any, propertyKey: string) => any
    {
        return (target: any, propertyKey: string) =>
        {
            if (!target[ComponentBase.CustomPropertyKey])
            {
                target[ComponentBase.CustomPropertyKey] = [];
            }
            (target[ComponentBase.CustomPropertyKey] as string[]).push(propertyKey);
        };
    }

    /**
     * Returns array of custom property keys.
     * @param obj Object fetching properties from.
     * @returns Array of property keys.
     */
    public static GetCustomProperties(obj: any): string[]
    {
        return obj ? obj[ComponentBase.CustomPropertyKey] || [] : [];
    }

    /**
     * Returns true if object has custom properties.
     * @param obj Target object.
     */
    public static HasCustomProperties(obj: any): boolean
    {
        return obj
            && obj[ComponentBase.CustomPropertyKey]
            && obj[ComponentBase.CustomPropertyKey].length;
    }

    // #endregion
}
